import React, { useMemo, useState } from "react";

type Item = {
  id: string;
  label: string;
  isTarget: boolean;
};

export function AttentionSelectiveExercise() {
  const items: Item[] = useMemo(
    () => [
      { id: "1", label: "🔵", isTarget: true },
      { id: "2", label: "🔺", isTarget: false },
      { id: "3", label: "🟢", isTarget: true },
      { id: "4", label: "⭐", isTarget: false },
      { id: "5", label: "🟡", isTarget: true },
      { id: "6", label: "🔴", isTarget: true },
      { id: "7", label: "⬛", isTarget: false },
      { id: "8", label: "⬜", isTarget: false },
      { id: "9", label: "🟣", isTarget: true },
      { id: "10", label: "🔻", isTarget: false },
      { id: "11", label: "🟠", isTarget: true },
      { id: "12", label: "◆", isTarget: false },
    ],
    []
  );

  const [clickedIds, setClickedIds] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);

  function toggleClick(id: string) {
    setClickedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  const correctClicks = clickedIds.filter((id) => {
    const item = items.find((i) => i.id === id);
    return item?.isTarget;
  }).length;

  const errors = clickedIds.filter((id) => {
    const item = items.find((i) => i.id === id);
    return item && !item.isTarget;
  }).length;

  const totalTargets = items.filter((i) => i.isTarget).length;

  return (
    <div className="max-w-xl mx-auto rounded-2xl border p-6 shadow-sm bg-white dark:bg-slate-900 dark:border-slate-700">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Exercice cognitif
          </p>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Attention sélective : clique seulement sur les cercles/couleurs rondes
          </h2>
        </div>
        <span className="text-[11px] px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
          Cibles : {totalTargets}
        </span>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
        Consigne : sélectionne uniquement les formes circulaires (ou emojis ronds). Les autres sont des distracteurs.
      </p>

      <div className="grid gap-3 grid-cols-3 md:grid-cols-4">
        {items.map((item) => {
          const isActive = clickedIds.includes(item.id);
          const isTarget = item.isTarget;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => toggleClick(item.id)}
              className={`w-full aspect-square flex items-center justify-center rounded-lg border text-2xl transition ${
                isActive
                  ? isTarget
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-100"
                    : "border-red-400 bg-red-50 text-red-700 dark:border-red-400 dark:bg-red-900/30 dark:text-red-100"
                  : "border-slate-200 bg-white text-slate-800 hover:border-blue-200 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-blue-500/50 dark:hover:bg-slate-800/80"
              }`}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-2 mt-5">
        <button
          type="button"
          onClick={() => setShowResult(true)}
          className="px-4 py-1.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500"
        >
          Voir le résultat
        </button>
        <button
          type="button"
          onClick={() => {
            setClickedIds([]);
            setShowResult(false);
          }}
          className="px-3 py-1.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
        >
          Réinitialiser
        </button>
      </div>

      {showResult && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
          <p className="font-semibold mb-1">Résultat</p>
          <p>
            Clics corrects : <span className="font-semibold">{correctClicks}</span> / {totalTargets}
          </p>
          <p>
            Erreurs : <span className="font-semibold">{errors}</span>
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">
            {errors === 0
              ? "Super focus !"
              : errors <= 2
              ? "Bon travail, encore un peu de vigilance."
              : "Refais un essai pour améliorer ta précision."}
          </p>
        </div>
      )}
    </div>
  );
}
